/**
 * Client de recherche Meilisearch dédié aux entités (auteurs, éditeurs, relieurs)
 *
 * Principe SOLID: Single Responsibility
 * - Point de création unique du searchClient pour les entités
 * - Séparation d'avec le searchClient du moteur livres
 *
 * Principe Singleton:
 * - Une seule instance par clé API
 * - Réutilisation pour éviter les reconfigurations
 *
 * @module entitySearchClient
 */

import type { InstantMeiliSearchInstance } from '@meilisearch/instant-meilisearch';
import { instantMeiliSearch } from '@meilisearch/instant-meilisearch';
import { MatchingStrategies } from 'meilisearch';

import { Env } from '@/lib/Env';

// ============================================================================
// Singleton Cache
// ============================================================================

let entitySearchClientCache: InstantMeiliSearchInstance | null = null;
let lastApiKey: string | null = null;

// ============================================================================
// Factory Function
// ============================================================================

/**
 * Crée ou retourne le client de recherche Meilisearch pour les entités
 *
 * @param apiKey - Clé API Meilisearch (publique)
 * @returns Instance du searchClient configurée pour les index entités
 *
 * @example
 * ```typescript
 * const searchClient = createEntitySearchClient(apiKey);
 * ```
 */
export function createEntitySearchClient(
  apiKey: string,
): InstantMeiliSearchInstance {
  // Réutilise le client si la clé API est la même (Singleton)
  if (entitySearchClientCache && lastApiKey === apiKey) {
    return entitySearchClientCache;
  }

  const { searchClient } = instantMeiliSearch(
    Env.NEXT_PUBLIC_MEILISEARCH_HOST,
    apiKey,
    {
      primaryKey: 'id',
      placeholderSearch: true,
      requestInit: {
        next: { revalidate: 1, tags: ['search-engine-entities'] },
      },
      meiliSearchParams: {
        matchingStrategy: MatchingStrategies.ALL,
        // PERFORMANCE: Désactiver le highlighting par défaut
        // Non nécessaire pour les pages entités (cartes simples)
        attributesToHighlight: [],
      },
    },
  );

  // Cache l'instance
  entitySearchClientCache = searchClient;
  lastApiKey = apiKey;

  return searchClient;
}
